import { Injectable } from '@angular/core';
import { getUserEmail } from '@fantaz/shared/auth';
import { TournamentApiService } from '@fantaz/shared/data-access/api';
import {
  TournamentType,
  TournamentsQueryParams,
  TournamentsResponse,
} from '@fantaz/shared/data-access/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, forkJoin, of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import * as TournamentsActions from './tournaments.actions';
import * as TournamentsSelector from './tournaments.selectors';

@Injectable()
export class TournamentsEffects {
  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TournamentsActions.loadTournaments),
        concatLatestFrom(() => {
          return this.store.select(TournamentsSelector.getTournamentParams);
        }),
        switchMap(([, params]) => {
          const requests$ = this.buildRequests(params);
          return forkJoin(requests$).pipe(
            map(({ CASH: cashTournaments, PRIZE: prizeTournaments }) => {
              return TournamentsActions.loadTournamentsSuccess({
                cashTournaments,
                prizeTournaments,
              });
            }),
            catchError((error) =>
              of(TournamentsActions.loadTournamentsFailure({ error })),
            ),
          );
        }),
      ),
    { dispatch: true },
  );
  loadPageTournaments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TournamentsActions.loadPageTournaments),
        concatLatestFrom(() => {
          return this.store.select(TournamentsSelector.getTournamentParams);
        }),
        concatMap(([, params]) => {
          const requests$ = this.buildRequests(params);

          return forkJoin(requests$).pipe(
            map(({ CASH: cashTournaments, PRIZE: prizeTournaments }) => {
              return TournamentsActions.loadPageTournamentsSuccess({
                cashTournaments,
                prizeTournaments,
              });
            }),
            catchError((error) =>
              of(TournamentsActions.loadPageTournamentsFailure({ error })),
            ),
          );
        }),
      ),
    { dispatch: true },
  );
  loadMoreTournaments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TournamentsActions.loadMoreTournaments),
        concatLatestFrom(() => {
          return this.store.select(TournamentsSelector.getTournamentParams);
        }),
        concatMap(([, params]) => {
          const requests$ = this.buildRequests(params);

          return forkJoin(requests$).pipe(
            map(({ CASH: cashTournaments, PRIZE: prizeTournaments }) => {
              return TournamentsActions.loadMoreTournamentsSuccess({
                cashTournaments,
                prizeTournaments,
              });
            }),
            catchError((error) =>
              of(TournamentsActions.loadMoreTournamentsFailure({ error })),
            ),
          );
        }),
      ),
    { dispatch: true },
  );
  filterTournaments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TournamentsActions.filterTournaments),
        concatLatestFrom(() => {
          return this.store.select(TournamentsSelector.getTournamentParams);
        }),
        concatMap(([_, params]) => {
          const requests$ = this.buildRequests(params);

          return forkJoin(requests$).pipe(
            map(({ CASH: cashTournaments, PRIZE: prizeTournaments }) => {
              return TournamentsActions.filterTournamentsSuccess({
                cashTournaments,
                prizeTournaments,
              });
            }),
            catchError((error) =>
              of(TournamentsActions.filterTournamentsFailure({ error })),
            ),
          );
        }),
      ),
    { dispatch: true },
  );
  loadSubscriptionTournament$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TournamentsActions.loadSubscriptionTournament),
        withLatestFrom(this.store.select(getUserEmail)),
        switchMap(([{ eventPrototypeUuid, eventSchedule }, email]) => {
          if (email) {
            return this.tournamentApiService
              .getSubscriptionStatus({
                eventPrototypeUuid,
                eventScheduleUuid: eventSchedule.uuid,
                email,
              })
              .pipe(
                map((data) => {
                  return TournamentsActions.loadSubscriptionTournamentSuccess({
                    eventPrototypeUuid,
                    eventSchedule,
                    status: data.status,
                  });
                }),
                catchError((error) => {
                  return of(
                    TournamentsActions.loadSubscriptionTournamentFailure({
                      error,
                    }),
                  );
                }),
              );
          } else {
            return [];
          }
        }),
      ),
    { dispatch: true },
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private tournamentApiService: TournamentApiService,
  ) {}

  buildRequests(
    params: TournamentsQueryParams,
  ): Record<string, Observable<TournamentsResponse>> {
    const {
      gameIds,
      tournamentType,
      getEsRanges,
      prizeValueRanges,
      entryFeeRanges,
      tournamentTags,
      cashPageIndex,
      prizePageIndex,
      cashPageSize,
      prizePageSize,
      search,
    } = params;

    const requests$: Record<string, Observable<TournamentsResponse>> = {
      CASH: this.tournamentApiService.fetchPromoteTournament({
        ...(gameIds && { gameIds }),
        ...(prizeValueRanges && { prizeValueRanges }),
        ...(entryFeeRanges && { entryFeeRanges }),
        ...(tournamentTags && { tournamentTags }),
        ...(search && { search }),
        getEsRanges,
        pageIndex: cashPageIndex,
        pageSize: cashPageSize,
        tournamentType: TournamentType.CASH,
      }),
      PRIZE: this.tournamentApiService.fetchPromoteTournament({
        ...params,
        tournamentType: TournamentType.PRIZE,
        pageIndex: prizePageIndex,
        pageSize: prizePageSize,
      }),
    };

    if (tournamentType === TournamentType.ALL) {
      return requests$;
    }

    return { [tournamentType]: requests$[tournamentType] };
  }
}
